import { Assets, Container, Graphics, Rectangle, Sprite, Text, Texture } from "pixi.js";
import { constrainWorldPosition, outfitFromParts, type AvatarParts, type AvatarType, type Direction, type EmoteType, type OutfitId } from "@aproape/shared";
import { avatarOverlayOffset, idleBreathScale, spritePairIndex } from "./animation";

export const AVATAR_LAYER_ORDER = ["body", "label", "status", "bubble"] as const;

export interface AvatarSnapshot {
  id: string;
  userId: string;
  displayName: string;
  avatarType: AvatarType;
  avatarParts: AvatarParts;
  createdAt: number;
  x: number;
  y: number;
  direction: Direction;
  moving: boolean;
  away: boolean;
  lounging: boolean;
  loungeRotation: number;
  emote: EmoteType | "";
  emoteUntil: number;
}

interface SpriteFrames {
  idle: [Texture, Texture];
  walk: [Texture, Texture];
  scale: number;
  bottomPaddings: readonly number[];
  frameScaleFactors: readonly number[];
}

const SPRITE_SHEET_URL = "/assets/avatars/outfit-sprites-v2.png";
const OUTFIT_ROWS: Record<OutfitId, number> = {
  female_sport: 0,
  female_noir: 1,
  female_blush: 2,
  male_urban: 3,
  male_sand: 4,
  male_varsity: 5,
};
const ROW_BOUNDS = [
  { top: 9, bottom: 253 },
  { top: 253, bottom: 494 },
  { top: 491, bottom: 723 },
  { top: 722, bottom: 920 },
  { top: 917, bottom: 1087 },
  { top: 1088, bottom: 1254 },
] as const;
const COLUMN_CENTERS = [201, 481, 758, 1048] as const;
const FRAME_WIDTH = 120;
// Alpha bounds measured inside each atlas cell. Scaling against the first idle
// frame keeps every pose the same visible height; bottom padding keeps shoes on
// one ground line without allowing pixels from neighbouring rows into a frame.
const FRAME_METRICS: Record<OutfitId, { visualHeights: readonly number[]; bottomPaddings: readonly number[] }> = {
  female_sport: { visualHeights: [236, 236, 236, 236], bottomPaddings: [4, 4, 4, 4] },
  female_noir: { visualHeights: [234, 234, 234, 234], bottomPaddings: [3, 3, 3, 3] },
  female_blush: { visualHeights: [224, 224, 224, 224], bottomPaddings: [4, 4, 4, 4] },
  male_urban: { visualHeights: [191, 191, 191, 191], bottomPaddings: [3, 3, 3, 3] },
  male_sand: { visualHeights: [166, 166, 167, 166], bottomPaddings: [1, 1, 0, 0] },
  male_varsity: { visualHeights: [161, 160, 154, 154], bottomPaddings: [3, 3, 4, 4] },
};
const framesByOutfit = new Map<OutfitId, SpriteFrames>();

export async function loadAvatarSprites(): Promise<void> {
  if (framesByOutfit.size) return;
  const atlas = await Assets.load<Texture>(SPRITE_SHEET_URL);
  for (const [outfit, row] of Object.entries(OUTFIT_ROWS) as Array<[OutfitId, number]>) {
    const bounds = ROW_BOUNDS[row]!;
    const frameHeight = bounds.bottom - bounds.top;
    const frame = (column: number) => new Texture({
      source: atlas.source,
      frame: new Rectangle(COLUMN_CENTERS[column]! - FRAME_WIDTH / 2, bounds.top, FRAME_WIDTH, frameHeight),
    });
    const metrics = FRAME_METRICS[outfit];
    const idleVisualHeight = metrics.visualHeights[0]!;
    framesByOutfit.set(outfit, {
      idle: [frame(0), frame(1)],
      walk: [frame(2), frame(3)],
      scale: 196 / frameHeight,
      bottomPaddings: metrics.bottomPaddings,
      frameScaleFactors: metrics.visualHeights.map((height) => idleVisualHeight / height),
    });
  }
}

function framesFor(snapshot: AvatarSnapshot): SpriteFrames {
  const outfit = outfitFromParts(snapshot.avatarType, snapshot.avatarParts);
  const frames = framesByOutfit.get(outfit);
  if (!frames) throw new Error(`Avatar sprites were not loaded for ${outfit}`);
  return frames;
}

export class AvatarView extends Container {
  readonly snapshot: AvatarSnapshot;
  private readonly bodyLayer = new Container();
  private readonly labelLayer = new Container();
  private readonly statusLayer = new Container();
  private readonly bubbleLayer = new Container();
  private readonly sprite: Sprite;
  private readonly nameText: Text;
  private readonly frames: SpriteFrames;
  private readonly sleepText: Text;
  private bubbleBg?: Graphics;
  private bubbleText?: Text;
  private bubbleUntil = 0;
  private readonly typingIndicator = new Container();
  private readonly typingDots: Graphics[] = [];
  private typing = false;
  private typingPhase = 0;
  private animationElapsed = 0;
  private sleepPhase = 0;
  private targetX: number;
  private targetY: number;

  constructor(snapshot: AvatarSnapshot, readonly isSelf: boolean) {
    super();
    this.snapshot = snapshot;
    this.targetX = snapshot.x;
    this.targetY = snapshot.y;
    this.position.set(snapshot.x, snapshot.y);
    this.frames = framesFor(snapshot);
    this.sprite = new Sprite(this.frames.idle[0]);
    this.sprite.anchor.set(0.5, 1);
    this.sprite.position.set(0, 8);
    this.sprite.scale.set(this.frames.scale);

    this.addChild(this.bodyLayer, this.labelLayer, this.statusLayer, this.bubbleLayer);
    const shadow = new Graphics().ellipse(0, 3, 39, 11).fill({ color: 0x60463f, alpha: 0.24 });
    this.bodyLayer.addChild(shadow);
    if (isSelf) {
      const marker = new Graphics().ellipse(0, 1, 43, 13).stroke({ color: 0xffffff, width: 3, alpha: 0.9 });
      this.bodyLayer.addChild(marker);
    }
    this.bodyLayer.addChild(this.sprite);

    this.nameText = new Text({ text: snapshot.displayName, style: { fontFamily: "Arial", fontSize: 14, fontWeight: "700", fill: 0x3f3142, stroke: { color: 0xffffff, width: 4 } } });
    this.nameText.anchor.set(0.5, 1);
    this.nameText.position.set(0, -202);
    this.labelLayer.addChild(this.nameText);

    this.sleepText = new Text({ text: "Zz", style: { fontFamily: "Arial", fontSize: 18, fontWeight: "700", fontStyle: "italic", fill: 0x725d8e, stroke: { color: 0xffffff, width: 5 } } });
    this.sleepText.anchor.set(0.5, 1);
    this.sleepText.position.set(30, -214);
    this.sleepText.visible = snapshot.away;
    this.statusLayer.addChild(this.sleepText);

    const typingBg = new Graphics().roundRect(-27, -16, 54, 32, 14).fill({ color: 0xffffff, alpha: 0.97 }).stroke({ color: 0xead8d3, width: 2 });
    this.typingIndicator.position.set(0, -246);
    this.typingIndicator.visible = false;
    this.typingIndicator.addChild(typingBg);
    for (const x of [-12, 0, 12]) {
      const dot = new Graphics().circle(0, 0, 4).fill({ color: 0x8b747e });
      dot.x = x;
      this.typingDots.push(dot);
      this.typingIndicator.addChild(dot);
    }
    this.bubbleLayer.addChild(this.typingIndicator);
  }

  setServerSnapshot(snapshot: Partial<AvatarSnapshot>): void {
    Object.assign(this.snapshot, snapshot);
    if (snapshot.displayName != null) this.nameText.text = snapshot.displayName;
    if (snapshot.x != null) this.targetX = snapshot.x;
    if (snapshot.y != null) this.targetY = snapshot.y;
  }

  predict(x: number, y: number, delta: number): void {
    if (!this.isSelf) return;
    const position = constrainWorldPosition(this.x + x * 190 * delta, this.y + y * 190 * delta);
    this.position.set(position.x, position.y);
  }

  update(delta: number): void {
    const smoothing = this.isSelf ? 0.12 : 0.22;
    this.x += (this.targetX - this.x) * smoothing;
    this.y += (this.targetY - this.y) * smoothing;
    this.animationElapsed += delta;

    const activeFrames = this.snapshot.moving ? this.frames.walk : this.frames.idle;
    const pairIndex = spritePairIndex(this.snapshot.avatarType, this.snapshot.moving, this.animationElapsed);
    const column = this.snapshot.moving ? pairIndex + 2 : pairIndex;
    this.sprite.texture = activeFrames[pairIndex]!;
    const facing = this.snapshot.direction === "left" ? -1 : 1;
    const frameScale = this.frames.scale * this.frames.frameScaleFactors[column]!;
    const idleFootInset = this.frames.bottomPaddings[0]! * this.frames.scale;
    const frameFootInset = this.frames.bottomPaddings[column]! * frameScale;
    const baseY = 8 - idleFootInset + frameFootInset;
    const idleScale = this.snapshot.moving ? { x: 1, y: 1 } : idleBreathScale(this.animationElapsed);
    this.sprite.scale.set(frameScale * facing * idleScale.x, frameScale * idleScale.y);
    this.sprite.position.set(0, baseY);
    this.sprite.rotation = 0;

    const overlayOffset = avatarOverlayOffset(this.snapshot.lounging);
    this.labelLayer.y = overlayOffset;
    this.statusLayer.y = overlayOffset;
    this.bubbleLayer.y = overlayOffset;

    if (this.snapshot.lounging) {
      this.sprite.position.set(0, baseY - 12);
      this.sprite.rotation = this.snapshot.loungeRotation;
    } else if (this.snapshot.emote && Date.now() < this.snapshot.emoteUntil) {
      this.applyEmoteAnimation(this.snapshot.emote, facing, baseY, frameScale);
    }

    this.sleepPhase += delta;
    this.sleepText.visible = this.snapshot.away;
    this.sleepText.y = -214 + Math.sin(this.sleepPhase * 2.4) * 2;
    if (Date.now() > this.bubbleUntil) this.clearBubble();
    this.typingPhase += delta;
    this.typingIndicator.visible = this.typing && !this.bubbleText && !this.snapshot.away;
    this.typingDots.forEach((dot, index) => {
      const wave = (Math.sin(this.typingPhase * 7 - index * 1.25) + 1) / 2;
      dot.y = -wave * 3;
      dot.alpha = .42 + wave * .58;
      dot.scale.set(.82 + wave * .22);
    });
  }

  private applyEmoteAnimation(emote: EmoteType, facing: number, baseY: number, frameScale: number): void {
    const now = performance.now();
    if (emote === "dance") {
      const wave = Math.sin(now / 125);
      this.sprite.x = wave * 7;
      this.sprite.y = baseY - Math.abs(Math.sin(now / 250)) * 8;
      this.sprite.rotation = wave * 0.08;
      return;
    }
    if (emote === "stunt") {
      const phase = now / 310;
      this.sprite.y = baseY - Math.abs(Math.sin(phase)) * 23;
      this.sprite.rotation = Math.sin(phase) * 0.58;
      return;
    }
    const hop = Math.abs(Math.sin(now / 260));
    this.sprite.y = baseY - hop * 27;
    this.sprite.scale.set(frameScale * facing * (1 - hop * 0.04), frameScale * (1 + hop * 0.04));
  }

  showBubble(text: string, until: number): void {
    this.clearBubble();
    const safe = text.length > 58 ? `${text.slice(0, 57)}…` : text;
    const label = new Text({ text: safe, style: { fontFamily: "Arial", fontSize: 13, fill: 0x3b2f3a, wordWrap: true, wordWrapWidth: 150, align: "center", lineHeight: 17 } });
    label.anchor.set(0.5, 1);
    label.position.set(0, -231);
    const bounds = label.getLocalBounds();
    const bg = new Graphics().roundRect(bounds.x - 10, -232 - bounds.height - 8, bounds.width + 20, bounds.height + 16, 12).fill({ color: 0xffffff, alpha: 0.96 }).stroke({ color: 0xf4b49e, width: 2 });
    this.bubbleLayer.addChild(bg, label);
    this.bubbleBg = bg;
    this.bubbleText = label;
    this.bubbleUntil = until;
  }

  setTyping(active: boolean): void {
    this.typing = active;
    this.typingIndicator.visible = active && !this.bubbleText && !this.snapshot.away;
  }

  isTyping(): boolean { return this.typing; }

  private clearBubble(): void {
    this.bubbleBg?.destroy();
    this.bubbleText?.destroy();
    this.bubbleBg = undefined;
    this.bubbleText = undefined;
    this.bubbleUntil = 0;
  }
}
